import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type { ApiError } from '../api/apiError';
import type {
  HolidayFormInput,
  HolidayRecord,
  HolidayType,
  MonthlyCalendarData,
  WorkingDayConfig,
} from '../types/officeCalendar';
import { todayBusinessCalendarDate } from '../utils/dateTime';
import { resolveMonthlyLeaveAccrual } from '../utils/monthWorkMetrics';
import {
  extractMonthNotes,
  holidayDateFromApi,
  isCalendarWeekOff,
  nextCalendarMonth,
  normalizeMonthlyCalendarData,
  selectUpcomingNamedHolidays,
  toHolidayApiPayload,
  UPCOMING_HOLIDAY_LOOKAHEAD_MONTHS,
  UPCOMING_NAMED_HOLIDAY_LIMIT,
  weekOffFormPayloads,
} from '../utils/officeCalendar';
import { CONFIGURATION_RECORD_ACTIVE } from '../constants/configuration';
import { getLeaveConfigurations } from './leaveConfigurationService';
import { getLeaveDashboard } from './leaveService';

type Envelope<T> = { success: boolean; message: string; data: T };

/**
 * Monthly leave accrual for Office Calendar summary.
 * HR Admin reads org leave policies; employees cannot, so the same
 * resolveMonthlyLeaveAccrual calculation uses their leave dashboard balances.
 */
export async function getOfficeCalendarMonthlyLeaveAccrual(
  year: number,
): Promise<number> {
  const leaveConfigs = await getLeaveConfigurations('', {
    isActive: CONFIGURATION_RECORD_ACTIVE,
  }).catch(() => null);
  if (leaveConfigs) {
    return resolveMonthlyLeaveAccrual(leaveConfigs.items);
  }
  const dashboard = await getLeaveDashboard(year).catch(() => null);
  if (!dashboard) return 0;
  return resolveMonthlyLeaveAccrual(dashboard.balances);
}

export async function getMonthlyCalendar(
  year: number,
  month: number,
  holidayCalendarId?: number | null,
): Promise<MonthlyCalendarData> {
  const response = await apiClient.get<Envelope<MonthlyCalendarData>>(
    API_ENDPOINTS.CALENDAR.MONTHLY,
    {
      params: {
        year,
        month,
        holidayCalendarId: holidayCalendarId ?? undefined,
      },
    },
  );
  return normalizeMonthlyCalendarData(response.data.data);
}

function textOrNull(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

function holidayRowsFromPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const row = payload as Record<string, unknown>;
  if (Array.isArray(row.holidays)) return row.holidays;
  if (Array.isArray(row.items)) return row.items;
  return [];
}

function holidayRecordFromApi(row: unknown): HolidayRecord | null {
  if (!row || typeof row !== 'object') return null;
  const item = row as Record<string, unknown>;
  const holidayDate = holidayDateFromApi(item.holidayDate ?? item.holiday_date);
  const holidayName = textOrNull(item.holidayName ?? item.holiday_name);
  if (!holidayDate || !holidayName) return null;
  const holidayId = Number(item.holidayId ?? item.holiday_id);
  const holidayType = textOrNull(item.holidayType ?? item.holiday_type);
  return {
    holidayId: Number.isFinite(holidayId) ? holidayId : 0,
    holidayDate,
    holidayName,
    holidayType: (holidayType as HolidayType | null) ?? 'OTHER',
    description: textOrNull(item.description),
    dayName: textOrNull(item.dayName ?? item.day_name),
    displayDate: textOrNull(item.displayDate ?? item.display_date),
  };
}

function dedicatedUpcomingEndpointMissing(error: unknown): boolean {
  const statusCode = (error as ApiError).statusCode;
  return statusCode === 404 || statusCode === 405;
}

async function getUpcomingHolidaysFromMonthlyCalendar(
  today: string,
  limit: number,
): Promise<HolidayRecord[]> {
  const collected: HolidayRecord[] = [];
  let year = Number(today.slice(0, 4));
  let month = Number(today.slice(5, 7));
  for (let index = 0; index < UPCOMING_HOLIDAY_LOOKAHEAD_MONTHS; index += 1) {
    const monthData = await getMonthlyCalendar(year, month);
    collected.push(...monthData.holidays);
    const upcoming = selectUpcomingNamedHolidays(collected, today, limit);
    if (upcoming.length >= limit) return upcoming;
    const next = nextCalendarMonth(year, month);
    year = next.year;
    month = next.month;
  }
  return selectUpcomingNamedHolidays(collected, today, limit);
}

/**
 * Upcoming named holidays for the signed-in employee's organization.
 * Prefers GET /calendar/upcoming-holidays (sp_cal_get_upcoming_holidays).
 * If that route is not mounted, uses the existing monthly Office Calendar API.
 */
export async function getUpcomingHolidays(
  today: string = todayBusinessCalendarDate(),
  limit: number = UPCOMING_NAMED_HOLIDAY_LIMIT,
  holidayCalendarId?: number | null,
): Promise<HolidayRecord[]> {
  try {
    const response = await apiClient.get<Envelope<unknown>>(
      API_ENDPOINTS.CALENDAR.UPCOMING_HOLIDAYS,
      {
        params: {
          fromDate: today,
          limit,
          holidayCalendarId: holidayCalendarId ?? undefined,
        },
      },
    );
    const holidays = holidayRowsFromPayload(response.data.data)
      .map(holidayRecordFromApi)
      .filter((row): row is HolidayRecord => row != null)
      .filter(holiday => !isCalendarWeekOff(holiday));
    return selectUpcomingNamedHolidays(holidays, today, limit);
  } catch (error) {
    if (!dedicatedUpcomingEndpointMissing(error)) throw error;
    return getUpcomingHolidaysFromMonthlyCalendar(today, limit);
  }
}

export async function createHoliday(
  input: HolidayFormInput,
  holidayCalendarId?: number | null,
): Promise<HolidayRecord> {
  const response = await apiClient.post<Envelope<{ holiday: HolidayRecord }>>(
    API_ENDPOINTS.CALENDAR.CREATE_HOLIDAY,
    {
      ...toHolidayApiPayload(input),
      holidayCalendarId: holidayCalendarId ?? undefined,
    },
  );
  return response.data.data.holiday;
}

export async function createWeekOffs(
  dates: string[],
  year: number,
  month: number,
  holidayCalendarId?: number | null,
): Promise<HolidayRecord[]> {
  const payloads = weekOffFormPayloads(dates, year, month);
  const created: HolidayRecord[] = [];
  for (const payload of payloads) {
    created.push(await createHoliday(payload, holidayCalendarId));
  }
  return created;
}

export async function updateHoliday(
  holidayId: number,
  input: HolidayFormInput,
): Promise<HolidayRecord> {
  const response = await apiClient.put<Envelope<{ holiday: HolidayRecord }>>(
    API_ENDPOINTS.CALENDAR.UPDATE_HOLIDAY(holidayId),
    toHolidayApiPayload(input),
  );
  return response.data.data.holiday;
}

export async function deleteHoliday(holidayId: number): Promise<void> {
  await apiClient.delete(API_ENDPOINTS.CALENDAR.DELETE_HOLIDAY(holidayId));
}

export async function getWorkingDaysConfig(): Promise<WorkingDayConfig[]> {
  const response = await apiClient.get<
    Envelope<{ workingDaysConfig: WorkingDayConfig[] }>
  >(API_ENDPOINTS.CALENDAR.WORKING_DAYS);
  return response.data.data.workingDaysConfig;
}

export async function getCalendarMonthNotes(
  year: number,
  month: number,
  holidayCalendarId?: number | null,
): Promise<string | null> {
  const response = await apiClient.get<Envelope<unknown>>(
    API_ENDPOINTS.CALENDAR.MONTH_NOTES,
    {
      params: {
        year,
        month,
        holidayCalendarId: holidayCalendarId ?? undefined,
      },
    },
  );
  return extractMonthNotes(response.data.data);
}

export async function saveCalendarMonthNotes(
  year: number,
  month: number,
  notes: string,
  holidayCalendarId?: number | null,
): Promise<string> {
  const response = await apiClient.put<Envelope<unknown>>(
    API_ENDPOINTS.CALENDAR.MONTH_NOTES,
    {
      year,
      month,
      notes,
      holidayCalendarId: holidayCalendarId ?? undefined,
    },
  );
  const saved = extractMonthNotes(response.data.data);
  return saved;
}
